import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Mail, Phone } from "lucide-react";
import { UpcomingEventsTable } from "@/components/dashboard/UpcomingEventsTable";
import { Skeleton } from "@/components/ui/skeleton";
import { getHostProfile } from "@/lib/queries/profiles";
import { getHostEvents } from "@/lib/queries/events";

export default function NPOHostDetails({ session, profile }: { session: any; profile: any }) {
  const { id } = useParams();

  const { data: hostData, isLoading: hostLoading } = useQuery({
    queryKey: ["host-details", id],
    queryFn: () => getHostProfile(id),
    enabled: !!id,
  });

  const { data: hostEvents = { data: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 1 } }, isLoading: eventsLoading } = useQuery({
    queryKey: ["host-events", id],
    queryFn: () => getHostEvents(session?.access_token!, 10),
    enabled: !!id && !!session?.access_token,
  });

  if (hostLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-1/3" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-1/4" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!hostData) {
    return <div className="text-center py-8">Host not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-4xl font-bold tracking-tight">Host Details</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="text-lg">
                {hostData.full_name.split(" ").map(n => n[0]).join("")}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-2xl font-semibold">{hostData.full_name}</h3>
              <p className="text-sm text-muted-foreground">Pronouns: {hostData.pronouns}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Mail className="h-5 w-5 text-gray-500" />
            <span>{hostData.email}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Phone className="h-5 w-5 text-gray-500" />
            <span>
              ({hostData.isd_code}) {hostData.phone}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Events History</CardTitle>
        </CardHeader>
        <CardContent>
          {eventsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : hostEvents.data.length > 0 ? (
            <UpcomingEventsTable events={hostEvents.data} />
          ) : (
            <p className="text-center py-4 text-muted-foreground">
              No events found for this host
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}